/**
 * The voice catalog, personas and agents — §5 of the voice workflow architecture.
 *
 * THREE INDEPENDENT AXES, and this file holds two of them. A persona is WHO the
 * patient hears; an agent is WHAT TECHNOLOGY carries it. Keeping them apart is
 * the whole point: it makes "same voice, different stack" and "same stack,
 * different voice" two runs rather than a forked config, which is the only way
 * a success-rate comparison is attributable to the thing that moved.
 *
 * SHARED, not livekit-local, for the reason `call-playbook.ts` is: the API
 * validates what an operator picked, the web app renders the picker, and the
 * voice worker realizes it. Three packages, one list — a second copy would
 * drift the first time a voice was added.
 */

import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// The catalog — where provider ids are hidden
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A provider a voice can be realized on. Not the same axis as `stack`: one
 * realtime agent and one pipeline agent may both realize the same catalog
 * voice, through different keys.
 */
export const REALIZATION_KEYS = ['openai-realtime', 'cartesia'] as const;
export type RealizationKey = (typeof REALIZATION_KEYS)[number];

/**
 * One catalog voice.
 *
 * `gender` and `accent` are properties of the VOICE, never fields on a persona
 * — a practice picks "Aria, feminine, American" and the traits come with her.
 * Storing them beside a voice that contradicts them is a state the schema would
 * then have to police; making them attributes of the thing picked means it
 * cannot arise.
 *
 * COVERAGE IS PER ENTRY AND IS NOT UNIVERSAL. OpenAI realtime ships a handful
 * of voices where Cartesia ships hundreds, so the catalog is the UNION and each
 * entry declares what it can be realized on. A persona on a voice with no
 * realization for the chosen agent is refused at dispatch, never substituted:
 * the patient hearing a different voice than the arm was configured with makes
 * the experiment unfalsifiable.
 */
export interface CatalogVoice {
  id: string;
  displayName: string;
  gender: 'feminine' | 'masculine' | 'neutral';
  accent: string;
  languages: readonly string[];
  realizations: Partial<Record<RealizationKey, Record<string, string>>>;
}

/**
 * The catalog. ONE ENTRY TODAY, and that is the honest state — Iris's current
 * voice on each stack, named once so neither stack owns it.
 *
 * `marin` is what the realtime session already uses; the Cartesia id is the
 * "Iris — Friendly Specialist" voice the pipeline already points at. Nothing
 * here is new behaviour; it is the two hardcoded ids becoming one named thing.
 */
export const VOICE_CATALOG: readonly CatalogVoice[] = [
  {
    id: 'iris',
    displayName: 'Iris',
    gender: 'feminine',
    accent: 'american',
    languages: ['en-US', 'es-MX'],
    realizations: {
      'openai-realtime': { voice: 'marin' },
      cartesia: { voiceId: 'c894559e-d529-4d70-a6fb-3330ecf7ef6b', model: 'sonic-3' },
    },
  },
];

export function catalogVoice(id: string): CatalogVoice | null {
  return VOICE_CATALOG.find((v) => v.id === id) ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Persona — what a practice actually picks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A persona names a VOICE, never a provider id.
 *
 * A voice id is stack-dependent — `marin` on OpenAI realtime, a UUID on
 * Cartesia. A persona storing one would silently change who the patient hears
 * the moment a component moved stacks, and would stop being reusable the moment
 * a second agent existed.
 */
export const personaSchema = z.object({
  id: z.string().min(1),
  /** Spoken identity — surfaces as `{{persona.name}}`. */
  name: z.string().min(1),
  /** A CATALOG voice id, never a provider's. */
  voice: z.string().min(1),
  locale: z.string().min(1),
  /** Consumed only by the pipeline's TTS. A realtime agent cannot honor it. */
  speed: z.number().positive().optional(),
});
export type Persona = z.infer<typeof personaSchema>;

/**
 * The seeded personas. ONE TODAY, deliberately.
 *
 * `name: 'Iris'` is the same identity `AGENT_NAME` holds today; this is where it
 * moves to, so that a second persona is a row rather than a code change.
 */
export const PERSONAS: readonly Persona[] = [
  { id: 'iris', name: 'Iris', voice: 'iris', locale: 'en-US' },
];

export function persona(id: string): Persona | null {
  return PERSONAS.find((p) => p.id === id) ?? null;
}

/**
 * WHO speaks on a call that named nobody.
 *
 * A ROW, NOT AN ENV VAR, and that is the whole point of this constant. The
 * deployment default used to be spelled `OPENAI_DEFAULT_VOICE` / `AGENT_NAME`
 * in one file and `WORKFLOW_TTS_VOICE` in another, so "who did that call use"
 * had two answers depending on which stack ran it. Naming the default persona
 * here means an unpicked call and a picked one resolve through the SAME path —
 * there is no second branch to reason about, and no way for the two to drift.
 */
export const DEFAULT_PERSONA: Persona = PERSONAS[0]!;

// ─────────────────────────────────────────────────────────────────────────────
// Agent — the technology
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One runnable stack: which providers, which models. Every agent runs the v2
 * workflow engine; `stack` says whether the runner drives a realtime model or
 * an STT → LLM → TTS pipeline.
 */
export const agentSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().min(1),
  stack: z.enum(['session', 'pipeline']),
  /** Which catalog key this agent resolves a persona's voice against. */
  realizes: z.enum(REALIZATION_KEYS),
  /** What it is made of, for the picker to show and an arm to record. */
  providers: z.object({
    realtime: z.string().optional(),
    stt: z.string().optional(),
    llm: z.string().optional(),
    tts: z.string().optional(),
  }),
  /** Persona controls this agent has no lever for. See `realize`. */
  cannotHonor: z.array(z.string()).default([]),
});
export type Agent = z.infer<typeof agentSchema>;

/**
 * The seeded agents. TWO, and the row is now the description of the stack
 * rather than a label on one the code hardcodes.
 *
 * `pipeline` names Gemini, which is a CHANGE of behaviour and not a record of
 * it: `pipeline-stack.ts` built an OpenAI LLM unconditionally until the agent
 * could say otherwise. `gemini-3.5-flash` is the model this repo already calls
 * for its classifiers, so the key and the client are proven — the pipeline
 * CONVERSATION on it is not, and no call has run it.
 */
export const AGENTS: readonly Agent[] = [
  {
    id: 'openai-realtime',
    displayName: 'OpenAI Realtime (speech-to-speech)',
    stack: 'session',
    realizes: 'openai-realtime',
    providers: { realtime: 'openai:gpt-realtime-2' },
    // There is no speech-rate control on the realtime path. Declared rather
    // than silently dropped — a slider that moves and changes nothing is the
    // worst outcome an experiment can have.
    cannotHonor: ['speed'],
  },
  {
    id: 'pipeline',
    displayName: 'Pipeline (Deepgram → Gemini → Cartesia)',
    stack: 'pipeline',
    realizes: 'cartesia',
    providers: { stt: 'deepgram:nova-3', llm: 'google:gemini-3.5-flash', tts: 'cartesia:sonic-3' },
    cannotHonor: [],
  },
];

export function agent(id: string): Agent | null {
  return AGENTS.find((a) => a.id === id) ?? null;
}

/**
 * WHAT carries a call that named no agent.
 *
 * Replaces `WORKFLOW_STACK` and the `WORKFLOW_STT_MODEL` / `WORKFLOW_LLM_MODEL`
 * / `WORKFLOW_TTS_MODEL` fallbacks: the stack a call runs on is now read from
 * an agent row in every case, picked or not.
 */
export const DEFAULT_AGENT: Agent = AGENTS[0]!;

// ─────────────────────────────────────────────────────────────────────────────
// The realization contract
// ─────────────────────────────────────────────────────────────────────────────

export type Realization =
  | { ok: true; voice: Record<string, string>; declined: readonly string[] }
  | { ok: false; reason: 'unavailable' | 'unrealizable'; detail: string };

/**
 * `realize(persona, agent)` — the ONLY coupling between the two upper axes.
 *
 * Two distinct failure modes, and both reject here rather than mid-call:
 *
 *   unavailable   the catalog voice has no realization on this agent
 *   unrealizable  the agent has no such lever at all
 *
 * Dispatch is the place to catch both. A made-up voice id fails at synthesis —
 * mid-call, after the caller has already been asked a question — and an
 * unrealizable persona is that same error one layer up, knowable before the
 * phone rings.
 *
 * `declined` is the softer half: a control the agent cannot honor does NOT fail
 * the pairing, because most personas leave `speed` at default and refusing them
 * all would be too strict. It is returned so the caller can say so out loud
 * instead of dropping it silently.
 */
export function realize(p: Persona, a: Agent): Realization {
  const voice = catalogVoice(p.voice);
  if (!voice) {
    return { ok: false, reason: 'unavailable', detail: `no catalog voice '${p.voice}'` };
  }
  const realization = voice.realizations[a.realizes];
  if (!realization) {
    return {
      ok: false,
      reason: 'unavailable',
      detail: `voice '${voice.displayName}' has no ${a.realizes} realization`,
    };
  }
  // Only controls the persona actually SET count as declined. A persona that
  // left `speed` alone is not asking for anything the agent must refuse.
  const declined = a.cannotHonor.filter((k) => (p as Record<string, unknown>)[k] !== undefined);
  return { ok: true, voice: realization, declined };
}

/** Which agents this persona can run on — what the picker greys out. */
export function agentsFor(p: Persona): readonly Agent[] {
  return AGENTS.filter((a) => realize(p, a).ok);
}

// ─────────────────────────────────────────────────────────────────────────────
// A call's voice — the one resolution every consumer reads
// ─────────────────────────────────────────────────────────────────────────────

/** What a call naming these ids speaks with. See `resolveCallVoice`. */
export interface CallVoice {
  /** The persona that speaks: the named one, else `DEFAULT_PERSONA`. */
  persona: Persona;
  /** The agent that carries it: the named one, else `DEFAULT_AGENT`. */
  agent: Agent;
  /** Ids the call named that the catalog does not hold; each fell back to the default. */
  unknown: { personaId?: string; agentId?: string };
  /** `realize(persona, agent)`, resolved once. */
  realization: Realization;
  /**
   * The realized voice, with the persona's `speed` attached (as a string, in the
   * same bag) when the agent does not decline it. Null when the pairing is refused.
   */
  voice: Record<string, string> | null;
}

/**
 * WHO speaks on a call and in WHICH voice, from the ids the call names (§5).
 *
 * The single resolution, so the voice worker (`run.ts`) and anything that
 * claims to sound like a call (the setup screens' voice preview, ADR-344)
 * cannot drift apart. An unknown id falls back to the default rather than
 * failing — an operator's typo must not be worse than not picking — and is
 * reported in `unknown` so the caller can say so.
 */
export function resolveCallVoice(ids: { personaId?: string; agentId?: string }): CallVoice {
  const namedPersona = ids.personaId ? persona(ids.personaId) : null;
  const namedAgent = ids.agentId ? agent(ids.agentId) : null;
  const p = namedPersona ?? DEFAULT_PERSONA;
  const a = namedAgent ?? DEFAULT_AGENT;
  const realization = realize(p, a);

  // `speed` is a PERSONA control the catalog does not carry — the realization
  // is the voice, this is how fast she speaks. Attached only when the agent did
  // not decline it, so a realtime agent (which has no speech-rate lever) never
  // receives a value it would silently drop.
  let voice: Record<string, string> | null = null;
  if (realization.ok) {
    const honorsSpeed = !realization.declined.includes('speed');
    voice =
      honorsSpeed && p.speed !== undefined
        ? { ...realization.voice, speed: String(p.speed) }
        : realization.voice;
  }

  return {
    persona: p,
    agent: a,
    unknown: {
      ...(ids.personaId && !namedPersona ? { personaId: ids.personaId } : {}),
      ...(ids.agentId && !namedAgent ? { agentId: ids.agentId } : {}),
    },
    realization,
    voice,
  };
}
